import { CursorClickIcon, ExclamationIcon } from '@heroicons/react/outline';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  SkeletonLoader,
  TextField,
  Title,
} from '@yoast/ui-library';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getGetBrandQueryKey,
  getGetBrandsQueryKey,
  useGetBrand,
  useGetQuerySuggestions,
  useUpdateBrandQueries,
} from '../../api/generated/insights';
import SparklesIcon from '../../assets/icon-sparkles-gradient.svg?react';
import PaperActions from '../../components/PaperActions';
import { useNavigationBlocker, useToast } from '../../hooks';

// Constants
const INITIAL_QUERY_COUNT = 5;
const LOADING_TEXT = 'Generating...';
const REGENERATING_TEXT = 'Re-generating...';
const MODAL_CLOSE_FOCUS_DELAY = 100; // ms - delay to ensure modal is closed before focusing
const MIN_LOADING_DURATION = 800; // ms - minimum loading duration for graceful UX
const SKELETON_PLACEHOLDERS = [
  '******************************',
  '***********************',
  '*******************************************************',
  '***********************************************',
  '***************************************************************',
  '*******************************************************',
  '*******************************************************',
  '***********************************************',
];

interface QueryItem {
  id: string;
  text: string;
  isRegenerating: boolean;
}

interface UpdateBrandQueriesError {
  error?: string;
  flagged_queries?: { query: string }[];
  message?: string;
}

const initialQueries: QueryItem[] = Array.from(
  { length: INITIAL_QUERY_COUNT },
  (_, index) => ({
    id: `query-${index}`,
    text: LOADING_TEXT,
    isRegenerating: true,
  }),
);

const createEmptyQueries = (): QueryItem[] =>
  Array.from({ length: INITIAL_QUERY_COUNT }, (_, index) => ({
    id: `query-${index}`,
    text: '',
    isRegenerating: false,
  }));

const QuerySelection: React.FC = () => {
  // All brandIds on this page should be based on the params,
  // NOT the actively selected brand in context
  const { brandId } = useParams();
  const NEXT_STEP = `/setup/${brandId}/analysis`;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // validate initial state only once
  const hasCheckedInitialStatus = React.useRef(false);

  // State variables
  const [queries, setQueries] = useState<QueryItem[]>(initialQueries);
  const [showRegenerateMode, setShowRegenerateMode] = useState(false);
  const [regenerateSelection, setRegenerateSelection] = useState<string[]>([]);
  const [allQueriesPool, setAllQueriesPool] = useState<string[]>([]);
  const [isAwaitingRegeneration, setIsAwaitingRegeneration] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showBrandNameWarning, setShowBrandNameWarning] = useState(false);

  // Navigation blocking - always block on this page
  const {
    showModal: showLeavePageModal,
    confirmLeave,
    cancelLeave,
  } = useNavigationBlocker({
    shouldBlock: true, // Always block navigation on this page
  });

  // has user closed the rate limit warning at least once
  const [isRateLimitAcknowledged, setIsRateLimitAcknowledged] = useState(false);

  // Moderation error state
  const [moderationError, setModerationError] = useState<{
    flaggedQueries: { query: string }[];
  } | null>(null);

  const {
    data: generatedQueries,
    isRefetching: isGeneratingQueries,
    error: generatedQueriesError,
    isError: isErrorGeneratingQueries,
    refetch: generateNewQueries,
  } = useGetQuerySuggestions(brandId || '', {
    query: { enabled: !!brandId },
  });

  // Get brand information for validation
  const { data: brandData } = useGetBrand(brandId || '', {
    query: { enabled: !!brandId },
  });

  // Helper function to check if a query text contains brand name
  const queryContainsBrandName = useCallback(
    (queryText: string): boolean => {
      if (!brandData?.data?.name) return false;

      const brandName = brandData.data.name.toLowerCase().trim();
      if (!brandName) return false;

      return queryText.toLowerCase().includes(brandName);
    },
    [brandData?.data?.name],
  );

  // Validation function to check if any queries contain brand name
  const hasQueriesWithBrandName = useCallback((): boolean => {
    return queries.some((query) => queryContainsBrandName(query.text));
  }, [queries, queryContainsBrandName]);

  // Modal handlers
  const handleCloseBrandWarning = () => {
    setShowBrandNameWarning(false);
  };

  const handleContinueWithoutChanging = () => {
    setShowBrandNameWarning(false);
    // Proceed with saving queries
    if (brandId) {
      saveQueries({
        brandId,
        data: {
          queries: queries.map((query) => query.text),
        },
      });
    }
  };

  const handleModifyQueries = () => {
    setShowBrandNameWarning(false);
    // After closing the modal, the user can manually edit their queries in the form below.

    // Focus on the first query input that contains the brand name
    const firstQueryWithBrandIndex = queries.findIndex((query) =>
      queryContainsBrandName(query.text),
    );

    if (firstQueryWithBrandIndex !== -1) {
      // Use setTimeout to ensure the modal is fully closed before focusing
      setTimeout(() => {
        const inputElement = document.getElementById(
          `query-text-${firstQueryWithBrandIndex}`,
        ) as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.select(); // Highlight all text in the input
        }
      }, MODAL_CLOSE_FOCUS_DELAY);
    }
  };

  /**
   * Check if the error is a moderation error from the API
   * @param error AxiosError instance
   *
   * @return Object with flagged queries if moderation error, otherwise null
   */
  function isModerationError(
    error: AxiosError<UpdateBrandQueriesError>,
  ): { flaggedQueries: { query: string }[] } | null {
    const moderationErrorMsg =
      'Moderation error: one or more queries flagged by OpenAI moderation endpoint.';
    const flaggedQueries = error?.response?.data?.flagged_queries;
    if (
      error?.response?.status === 422 &&
      error?.response?.data?.error === moderationErrorMsg &&
      Array.isArray(flaggedQueries)
    ) {
      return { flaggedQueries };
    }
    return null;
  }

  // PUT request to save queries
  const { mutate: saveQueries, isPending: isPendingSavingQueries } =
    useUpdateBrandQueries({
      mutation: {
        onSuccess: async (data) => {
          setErrorMessage(null);
          if (data?.status && data.status >= 200 && data.status < 300) {
            // Invalidate the brand query so the next page gets fresh data
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: getGetBrandQueryKey(brandId || ''),
              }),
              queryClient.invalidateQueries({
                queryKey: getGetBrandsQueryKey(),
              }),
            ]);
            navigate(NEXT_STEP);
          }
        },
        onError: (error: AxiosError<UpdateBrandQueriesError>) => {
          // Moderation error handling
          const moderationResult = isModerationError(error);
          if (moderationResult) {
            setModerationError({
              flaggedQueries: moderationResult.flaggedQueries,
            });
            return;
          }

          if (
            error?.response?.data?.message ===
            'Cannot update queries for a brand that has existing analyses.'
          ) {
            navigate(NEXT_STEP, {
              // replace history to avoid going "back" to the form
              replace: true,
            });
            return;
          }

          // Try to extract the error message from the response
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'An unexpected error occurred.';
          setErrorMessage(message);
        },
      },
    });

  // If brandId is not defined or valid, redirect to the previous step
  useEffect(() => {
    if (!brandId) {
      navigate('/setup', {
        replace: true,
      });
    }
  }, [brandId, navigate, toast]);

  // If brand status is not pendingQuerySelection, redirect to the correct step
  // Only check once on mount to avoid race conditions with user-initiated saves
  useEffect(() => {
    if (!brandData?.data?.setupStatus || hasCheckedInitialStatus.current) {
      return;
    }

    hasCheckedInitialStatus.current = true;

    // If the brand status is not pendingQuerySelection, redirect to analysis step
    if (brandData.data.setupStatus !== 'pendingQuerySelection') {
      // show a toast explaining
      toast('Queries cannot be changed for a brand with a pending analysis.', {
        variant: 'warning',
      });

      // bounce the user out with a redirect
      navigate(NEXT_STEP, {
        replace: true,
      });
    }
  }, [brandData?.data?.setupStatus, brandId, navigate, NEXT_STEP, toast]);

  useEffect(() => {
    // Initialize queries from API response
    if (
      generatedQueries?.data?.queries &&
      Array.isArray(generatedQueries.data.queries)
    ) {
      const suggestions = generatedQueries.data.queries.slice(
        0,
        INITIAL_QUERY_COUNT,
      );
      const newQueries = suggestions.map((queryText, index) => ({
        id: `query-${index}`,
        text: queryText,
        isRegenerating: false,
      }));
      setQueries(newQueries);
      // Remove the initial suggestions from the pool
      setAllQueriesPool(
        generatedQueries.data.queries.slice(INITIAL_QUERY_COUNT),
      );
    } else if (isErrorGeneratingQueries) {
      // If there's an error generating queries, set all queries to empty strings
      const emptyQueries = createEmptyQueries();
      setQueries(emptyQueries);
      setAllQueriesPool([]);
    }
  }, [generatedQueries, isErrorGeneratingQueries]);

  // Helper function to handle minimum loading duration and complete regeneration
  const replaceQueriesFromPool = useCallback(
    (updatedQueries: string[], shouldUpdatePool = false) => {
      // Always wait the full minimum loading duration for consistent UX
      setTimeout(() => {
        setQueries((prev) =>
          prev.map((query, index) => {
            const selIdx = regenerateSelection.indexOf(index.toString());
            return selIdx >= 0 && updatedQueries[selIdx]
              ? {
                  ...query,
                  text: updatedQueries[selIdx],
                  isRegenerating: false,
                }
              : { ...query, isRegenerating: false };
          }),
        );
        setRegenerateSelection([]);
        setIsAwaitingRegeneration(false);
        setShowRegenerateMode(false);

        // Only update pool after the timeout when using pool queries
        if (shouldUpdatePool) {
          setAllQueriesPool((prevPool) => {
            const used = new Set(updatedQueries);
            return prevPool.filter((queryText) => !used.has(queryText));
          });
        }
      }, MIN_LOADING_DURATION);
    },
    [regenerateSelection],
  );

  useEffect(() => {
    // Handle regeneration response only after explicit regeneration request
    if (isAwaitingRegeneration && !isGeneratingQueries) {
      if (
        generatedQueries?.data?.queries &&
        Array.isArray(generatedQueries.data.queries)
      ) {
        const newQueries = generatedQueries.data.queries.slice(
          0,
          INITIAL_QUERY_COUNT,
        );
        const selectedIndices = regenerateSelection.map(Number);

        // For API calls, complete immediately without additional timeout
        setQueries((prev) =>
          prev.map((query, index) => {
            const selectionIndex = selectedIndices.indexOf(index);
            return {
              ...query,
              text:
                selectionIndex >= 0 && newQueries[selectionIndex]
                  ? newQueries[selectionIndex]
                  : query.text,
              isRegenerating: false,
            };
          }),
        );
        setRegenerateSelection([]);
        setIsAwaitingRegeneration(false);
        setShowRegenerateMode(false);
      } else if (isErrorGeneratingQueries) {
        // If there's an error during regeneration, reset the regenerating state
        // but keep the original queries (don't replace with empty strings)
        setQueries((prev) =>
          prev.map((query) => ({
            ...query,
            isRegenerating: false,
          })),
        );
        setRegenerateSelection([]);
        setIsAwaitingRegeneration(false);
        setShowRegenerateMode(false);
      }
    }
  }, [
    generatedQueries,
    isGeneratingQueries,
    isAwaitingRegeneration,
    regenerateSelection,
    isErrorGeneratingQueries,
  ]);

  const getNextQueriesFromPool = useCallback(
    (count: number, exclude: string[]): string[] => {
      // Exclude queries currently shown
      const used = new Set(exclude);
      return allQueriesPool
        .filter((queryText) => !used.has(queryText))
        .slice(0, count);
    },
    [allQueriesPool],
  );

  // Memoized values for performance
  const isSubmitDisabled = useMemo(
    () =>
      isPendingSavingQueries ||
      isGeneratingQueries ||
      queries.length === 0 ||
      queries.some(
        (query) =>
          query.isRegenerating ||
          (query.text === LOADING_TEXT && !isErrorGeneratingQueries) ||
          query.text.trim() === '',
      ) ||
      showRegenerateMode,
    [
      isPendingSavingQueries,
      isGeneratingQueries,
      queries,
      showRegenerateMode,
      isErrorGeneratingQueries,
    ],
  );

  // Determine if initial queries are still loading
  const isInitialQueriesLoading =
    isGeneratingQueries ||
    (queries.some((q) => q.text === LOADING_TEXT) && !isErrorGeneratingQueries);

  // Action handlers
  const toggleRegenerateSelection = useCallback((index: number) => {
    const indexStr = index.toString();
    setRegenerateSelection((prev) =>
      prev.includes(indexStr)
        ? prev.filter((i) => i !== indexStr)
        : [...prev, indexStr],
    );
  }, []);

  const handleTextChange = useCallback((index: number, newText: string) => {
    setQueries((prev) =>
      prev.map((query, i) =>
        i === index ? { ...query, text: newText } : query,
      ),
    );
  }, []);

  const toggleRegenerateMode = () => {
    setShowRegenerateMode(!showRegenerateMode);
    setRegenerateSelection([]);
  };

  const handleRegenerateQueries = useCallback(() => {
    if (isGeneratingQueries || regenerateSelection.length === 0) return;

    // Mark selected queries as regenerating
    setQueries((prev) =>
      prev.map((query, index) => ({
        ...query,
        isRegenerating: regenerateSelection.includes(index.toString()),
      })),
    );

    // Get next available queries from pool
    const currentShown = queries.map((query) => query.text);
    const totalQueriesNeeded = regenerateSelection.length;
    const nextQueriesFromPool = getNextQueriesFromPool(
      totalQueriesNeeded,
      currentShown,
    );
    const isPoolSufficient = nextQueriesFromPool.length >= totalQueriesNeeded;

    // determine if we have enough in the pool
    if (isPoolSufficient) {
      // Replace selected queries with next from pool (fixed timeout)
      replaceQueriesFromPool(nextQueriesFromPool, true);
    } else {
      // Not enough in pool, fallback to API (fixed timeout like pool queries)
      setIsAwaitingRegeneration(true);
      generateNewQueries();
    }
  }, [
    isGeneratingQueries,
    regenerateSelection,
    generateNewQueries,
    queries,
    getNextQueriesFromPool,
    replaceQueriesFromPool,
  ]);

  const handleSubmitQueries = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setModerationError(null); // Clear moderation error on submit
      if (!brandId) return;

      // Check if any query contains the brand name
      if (hasQueriesWithBrandName()) {
        setShowBrandNameWarning(true);
        return;
      }

      // Proceed with saving queries if no brand name detected
      saveQueries({
        brandId,
        data: {
          queries: queries.map((query) => query.text),
        },
      });
    },
    [brandId, queries, saveQueries, hasQueriesWithBrandName],
  );

  const handleCloseRateLimitWarning = () => {
    setIsRateLimitAcknowledged(true);
  };

  return (
    <div>
      {/* Moderation Error Notification */}
      {moderationError && (
        <Alert className="yst-mb-4" variant="error">
          <strong>Moderation Error</strong>
          <div className="yst-mt-2">
            <span>
              Some of your queries include content that our AI moderation system
              can’t process.
            </span>
            <br />
            <span>These queries were flagged and won’t be saved:</span>
            <ul className="yst-ml-4 yst-list-disc">
              {moderationError.flaggedQueries.map((q, idx) => (
                <li key={idx}>{q.query}</li>
              ))}
            </ul>
            <br />
            <span>Edit or replace them to continue.</span>
          </div>
        </Alert>
      )}
      {/* Error Alert */}
      {errorMessage && (
        <Alert className="yst-mb-4" variant="error">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={handleSubmitQueries}>
        <div className="yst-mb-8">
          <Title size="2" className="yst-mb-2">
            Your queries
          </Title>
          <div className="yst-space-y-4">
            <p>You can edit your queries or regenerate new ones with AI.</p>

            <div
              className={cx(
                'yst-flex yst-gap-2',
                showRegenerateMode ? 'yst-justify-end' : 'yst-justify-start',
              )}
            >
              {showRegenerateMode ? (
                <>
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={toggleRegenerateMode}
                  >
                    Done
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleRegenerateQueries}
                    className="yst-gap-1"
                    isLoading={isGeneratingQueries || isAwaitingRegeneration}
                    disabled={
                      isAwaitingRegeneration ||
                      isGeneratingQueries ||
                      regenerateSelection.length === 0
                    }
                  >
                    <SparklesIcon aria-hidden="true" />
                    Regenerate selected queries
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={toggleRegenerateMode}
                  disabled={isInitialQueriesLoading || isErrorGeneratingQueries}
                >
                  <CursorClickIcon />
                  Select queries to regenerate
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="yst-mb-8">
          {queries.length === 0
            ? SKELETON_PLACEHOLDERS.map((value, index) => (
                <div
                  key={`placeholder-query-${index}`}
                  className={cx(
                    'yst-mb-4 yst-cursor-pointer yst-rounded yst-border yst-border-slate-300 yst-p-3 yst-transition-colors',
                    'yst-bg-white',
                  )}
                >
                  <SkeletonLoader>
                    {value ||
                      'Example placeholder query for demonstration purposes.'}
                  </SkeletonLoader>
                </div>
              ))
            : queries.slice(0, INITIAL_QUERY_COUNT).map((query, index) => {
                //TODO: At the moment we do a hard slice to just show the initial 5 queries. This should be improved later on to use the rest of the questions that are generated.
                const displayValue = query.isRegenerating
                  ? query.text === LOADING_TEXT
                    ? LOADING_TEXT
                    : REGENERATING_TEXT
                  : query.text;

                return (
                  <div key={query.id} className={cx('yst-mb-4')}>
                    <div className="yst-flex yst-gap-2">
                      {showRegenerateMode && (
                        <div className="yst-flex yst-items-center">
                          <Checkbox
                            id={`query-regenerate-${index}`}
                            name={`query-regenerate-${index}`}
                            value={index.toString()}
                            // label={query.text} // required prop but need to hide label itself? ui-lib could be updated?
                            checked={regenerateSelection.includes(
                              index.toString(),
                            )}
                            onChange={() => toggleRegenerateSelection(index)}
                            disabled={isGeneratingQueries}
                          />
                        </div>
                      )}
                      <div className="yst-flex-1">
                        <TextField
                          id={`query-text-${index}`}
                          label={`Query ${index + 1}`}
                          type="text"
                          value={displayValue}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (query.isRegenerating) return;
                            handleTextChange(index, e.target.value);
                          }}
                          placeholder="Enter your query..."
                          className="yst-w-full"
                          disabled={query.isRegenerating}
                          required
                          minLength={3}
                          maxLength={255}
                          validation={
                            queryContainsBrandName(query.text)
                              ? {
                                  variant: 'warning',
                                  message:
                                    'For more accurate insights, avoid using your brand name in the query.',
                                }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        <PaperActions
          buttonText="Run analysis (~ 5 minutes)"
          isButtonDisabled={isSubmitDisabled}
          isLoading={isPendingSavingQueries}
        />
      </form>

      {/* Rate Limit Warning Modal */}
      <Modal
        isOpen={
          // error
          isErrorGeneratingQueries &&
          // validate error type
          isAxiosError(generatedQueriesError) &&
          // error is rate limit
          generatedQueriesError?.response?.status === 429 &&
          // ensure has not been acknowledged before
          !isRateLimitAcknowledged
        }
        onClose={handleCloseRateLimitWarning}
        position="center"
        as="div"
      >
        <Modal.Panel>
          <div className="yst-flex yst-items-start yst-justify-stretch yst-gap-4">
            <div>
              <span className="yst-inline-flex yst-h-10 yst-w-10 yst-items-center yst-justify-center yst-rounded-full yst-bg-red-100">
                <ExclamationIcon className="yst-h-6 yst-w-6 yst-text-red-600" />
              </span>
            </div>
            <div className="yst-flex-1">
              <Modal.Container.Header className="yst-mb-3 yst-w-full">
                <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                  Query regeneration limit reached
                </Modal.Title>
              </Modal.Container.Header>
              <Modal.Container.Content className="yst-mb-6 yst-space-y-3">
                <p className="yst-text-sm yst-text-slate-600">
                  You’ve used all your query regenerations for this brand
                  analysis, but you can still edit your queries manually.
                </p>
              </Modal.Container.Content>
              <Modal.Container.Footer className="yst-flex yst-justify-end yst-gap-3">
                <Button variant="primary" onClick={handleCloseRateLimitWarning}>
                  Edit queries manually
                </Button>
              </Modal.Container.Footer>
            </div>
          </div>
        </Modal.Panel>
      </Modal>

      {/* Brand Name Warning Modal */}
      <Modal
        isOpen={showBrandNameWarning}
        onClose={handleCloseBrandWarning}
        position="center"
        as="div"
      >
        <Modal.Panel>
          <div className="yst-flex yst-items-start yst-justify-stretch yst-gap-4">
            <div>
              <span className="yst-inline-flex yst-h-10 yst-w-10 yst-items-center yst-justify-center yst-rounded-full yst-bg-amber-100">
                <ExclamationIcon className="yst-h-6 yst-w-6 yst-text-amber-600" />
              </span>
            </div>
            <div className="yst-flex-1">
              <Modal.Container.Header className="yst-mb-3 yst-w-full">
                <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                  Brand name detected in your queries
                </Modal.Title>
              </Modal.Container.Header>
              <Modal.Container.Content className="yst-mb-6 yst-space-y-3">
                <p className="yst-text-sm yst-text-slate-600">
                  We found your brand name in one or more of your queries. Using
                  your brand name in queries often show more mentions and more
                  positive sentiment, which can skew your results.
                </p>
                <p className="yst-text-sm yst-text-slate-600">
                  For more balanced results, remove your brand name or make the
                  queries more general.
                </p>
              </Modal.Container.Content>
              <Modal.Container.Footer className="yst-flex yst-justify-end yst-gap-3">
                <Button
                  onClick={handleContinueWithoutChanging}
                  variant="secondary"
                  disabled={isPendingSavingQueries}
                  isLoading={isPendingSavingQueries}
                >
                  <SparklesIcon />
                  Run analysis anyway
                </Button>
                <Button variant="primary" onClick={handleModifyQueries}>
                  Review and edit queries
                </Button>
              </Modal.Container.Footer>
            </div>
          </div>
        </Modal.Panel>
      </Modal>

      {/* Leave Page Warning Modal */}
      {/* TODO: move to its own component (and possibly tie it in with the hook?) */}
      <Modal
        isOpen={showLeavePageModal}
        onClose={cancelLeave}
        position="center"
        as="div"
      >
        <Modal.Panel>
          <div className="yst-flex yst-items-start yst-justify-stretch yst-gap-4">
            <div>
              <span className="yst-inline-flex yst-h-10 yst-w-10 yst-items-center yst-justify-center yst-rounded-full yst-bg-amber-100">
                <ExclamationIcon className="yst-h-6 yst-w-6 yst-text-amber-600" />
              </span>
            </div>
            <div className="yst-flex-1">
              <Modal.Container.Header className="yst-mb-3 yst-w-full">
                <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                  Leave this page?
                </Modal.Title>
              </Modal.Container.Header>
              <Modal.Container.Content className="yst-mb-6">
                <p className="yst-text-sm yst-text-slate-600">
                  You have unsaved changes. Finish setting up your brand to save
                  your queries. Leaving this page will discard your queries.
                </p>
              </Modal.Container.Content>
              <Modal.Container.Footer className="yst-flex yst-justify-end yst-gap-3">
                <Button onClick={cancelLeave} variant="secondary">
                  No, stay on page
                </Button>
                <Button variant="primary" onClick={confirmLeave}>
                  Yes, discard queries
                </Button>
              </Modal.Container.Footer>
            </div>
          </div>
        </Modal.Panel>
      </Modal>
    </div>
  );
};

export default QuerySelection;
